// Sanitizer used for any HTML we render via innerHTML from a source we don't
// fully control (quote-sent bodies, user-event comments authored as HTML,
// revision notes). Does two jobs:
//   • **Security** — drop `<script>` / `<style>` / `<iframe>` / other
//     active-content tags, strip `on*` handlers and `javascript:` URLs.
//   • **Aesthetics** — flatten email-templated chrome (layout tables,
//     <font>, inline style/class/width/etc.), drop empty blocks, and
//     collapse `<br><br>` runs.
// Content text (including &nbsp; characters) is preserved — the caller's
// CSS gives it a sensible line-height.
export function SanitizeRichHTML(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.body;

    // Drop script/style/active-content tags outright before anything else
    // touches the tree — we re-inject this HTML via innerHTML downstream.
    root.querySelectorAll('script, style, iframe, object, embed, link, meta, base').forEach($el => $el.remove());

    const dropAttrs = ['style', 'class', 'align', 'valign', 'bgcolor', 'color', 'border', 'cellpadding', 'cellspacing', 'width', 'height', 'face', 'size'];
    root.querySelectorAll<HTMLElement>('*').forEach($el => {
        for (const a of dropAttrs) {
            $el.removeAttribute(a);
        }
        // Strip any `on*` handlers and `javascript:` URLs.
        for (const attr of Array.from($el.attributes)) {
            const name = attr.name.toLowerCase();
            if (name.startsWith('on')) {
                $el.removeAttribute(attr.name);
                continue;
            }
            if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attr.value)) {
                $el.removeAttribute(attr.name);
            }
        }
    });

    const unwrap = ($el: Element) => {
        const parent = $el.parentNode;
        if (!parent) {
            return;
        }
        while ($el.firstChild) {
            parent.insertBefore($el.firstChild, $el);
        }
        $el.remove();
    };
    const unwrapTags = ['font', 'table', 'tbody', 'thead', 'tfoot', 'tr', 'td', 'th', 'center'];
    for (const tag of unwrapTags) {
        Array.from(root.querySelectorAll(tag)).forEach(unwrap);
    }
    // <span> is usually just styling — unwrap when it has no meaningful attrs left.
    Array.from(root.querySelectorAll('span')).forEach($s => {
        if ($s.attributes.length === 0) {
            unwrap($s);
        }
    });

    const isEmptyBlock = ($el: Element): boolean => {
        if ($el.querySelector('img, a, ul, ol, table')) {
            return false;
        }
        const text = ($el.textContent ?? '').replace(/[ \s]+/g, '');
        return text.length === 0;
    };
    const blockTags = ['p', 'div', 'section', 'article', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'];
    // Iterate until stable — removing an empty block can leave its parent empty too.
    let changed = true;
    while (changed) {
        changed = false;
        for (const tag of blockTags) {
            root.querySelectorAll(tag).forEach($el => {
                if (isEmptyBlock($el)) {
                    $el.remove();
                    changed = true;
                }
            });
        }
    }

    // Collapse consecutive <br>s to a single one — "<br><br>" would
    // otherwise force an extra blank line on top of the paragraph
    // spacing we already get from `<p>` / `<li>` etc.
    root.querySelectorAll('br').forEach($br => {
        let $next = $br.nextSibling;
        while ($next?.nodeType === Node.ELEMENT_NODE && ($next as Element).tagName === 'BR') {
            const $toRemove = $next;
            $next = $next.nextSibling;
            $toRemove.parentNode?.removeChild($toRemove);
        }
    });

    return root.innerHTML;
}

// TrimTrailingBreaks drops what a composer leaves at the end of a message:
// empty paragraphs, stray <br>s and whitespace-only text, including inside
// the last block. Nothing follows them, so printed they read as content that
// failed to load (#10628). Run it on sanitized or plain-to-HTML bodies alike.
export function TrimTrailingBreaks(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.body;

    const isBlank = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
            return (node.textContent ?? '').replace(/\s+/g, '') === '';
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return true;
        }
        const $el = node as Element;
        if ($el.tagName === 'BR') {
            return true;
        }
        if ($el.matches('img, a, ul, ol, table, video, hr') || $el.querySelector('img, a, ul, ol, table, video, hr')) {
            return false;
        }
        return ($el.textContent ?? '').replace(/\s+/g, '') === '';
    };

    // Trim a container's tail, then descend into its new last element and
    // trim that tail too, so `<p>text<br><br></p>` loses its breaks as well.
    let $parent: Element | null = root;
    while ($parent) {
        while ($parent.lastChild && isBlank($parent.lastChild)) {
            $parent.removeChild($parent.lastChild);
        }
        const $last: ChildNode | null = $parent.lastChild;
        if ($last?.nodeType === Node.ELEMENT_NODE && ($last as Element).tagName !== 'BR') {
            $parent = $last as Element;
        } else {
            $parent = null;
        }
    }

    return root.innerHTML;
}
